// Smarter local NLU for the THINKINGWORLD chatbot.
//
// How it works:
//   1. Normalize the user's text (lowercase, strip punctuation).
//   2. Tokenize, drop stopwords, expand synonyms (e.g. "cost" -> "price").
//   3. Score each intent by how many of its training-phrase tokens
//      overlap with the user's tokens, weighted by token rarity.
//   4. The best-scoring intent wins, IF its confidence clears a threshold.
//   5. Otherwise we return a graceful "let me connect you" fallback.

const STOPWORDS = new Set([
  'a','an','and','are','as','at','be','but','by','do','does','did','for','from','have','has','had',
  'i','in','is','it','its','me','my','of','on','or','our','please','tell','that','the','then','there',
  'they','this','to','us','was','we','were','what','when','where','which','who','why','will','with','would',
  'you','your','yours','can','could','should','need','want','like','just','about','some','any','give','show',
  'know','let','say','says','said','more','also','too','than','okay','ok','hey','hi','hello','yo','sup','hii'
]);

const SYNONYMS = {
  // pricing
  cost: 'price', costs: 'price', pricing: 'price', priced: 'price', rate: 'price',
  rates: 'price', fee: 'price', fees: 'price', charge: 'price', charges: 'price',
  budget: 'price', quote: 'price', quotation: 'price', estimate: 'price',
  expensive: 'price', cheap: 'price', affordable: 'price',

  // timeline
  timeline: 'duration', deadline: 'duration', schedule: 'duration', days: 'duration',
  weeks: 'duration', month: 'duration', months: 'duration', long: 'duration',
  time: 'duration', soon: 'duration', fast: 'duration', quick: 'duration', delivery: 'duration',

  // services
  services: 'service', offer: 'service', offering: 'service', do: 'service',
  build: 'service', develop: 'service', development: 'service',
  capability: 'service', capabilities: 'service', specialize: 'service',

  // projects
  projects: 'project', portfolio: 'project', work: 'project', works: 'project',
  example: 'project', examples: 'project', case: 'project', samples: 'project',
  showcase: 'project', past: 'project', previous: 'project',

  // contact
  contact: 'reach', reach: 'reach', call: 'reach', phone: 'reach', mobile: 'reach',
  number: 'reach', email: 'reach', mail: 'reach', whatsapp: 'reach', talk: 'reach',
  speak: 'reach', connect: 'reach', message: 'reach',

  // process
  process: 'process', methodology: 'process', approach: 'process', method: 'process',
  workflow: 'process', steps: 'process', stages: 'process',

  // tech
  tech: 'tech', technology: 'tech', technologies: 'tech', stack: 'tech',
  framework: 'tech', frameworks: 'tech', tool: 'tech', tools: 'tech',
  language: 'tech', languages: 'tech',

  // company / team
  company: 'company', team: 'company', studio: 'company', agency: 'company',
  founder: 'company', founders: 'company', staff: 'company',

  // hire / start
  hire: 'start', start: 'start', begin: 'start', engage: 'start',
  onboard: 'start', kickoff: 'start',

  // location
  located: 'location', based: 'location', office: 'location',
  city: 'location', country: 'location', remote: 'location',

  // medical project
  medical: 'medical', hospital: 'medical', clinic: 'medical', doctor: 'medical',
  doctors: 'medical', appointment: 'medical', appointments: 'medical',
  patient: 'medical', patients: 'medical', healthcare: 'medical',

  // namma project
  namma: 'namma', journey: 'namma', travel: 'namma', tourism: 'namma',
  trip: 'namma', itinerary: 'namma', tourist: 'namma', vacation: 'namma',

  // mobile
  app: 'mobile', apps: 'mobile', android: 'mobile', ios: 'mobile',
  iphone: 'mobile',

  // web
  web: 'web', website: 'web', websites: 'web', site: 'web', sites: 'web',
  webapp: 'web', portal: 'web',

  // desktop
  desktop: 'desktop', windows: 'desktop', mac: 'desktop', macos: 'desktop', linux: 'desktop',

  // ai
  ai: 'ai', llm: 'ai', llms: 'ai', chatbot: 'ai', gpt: 'ai', openai: 'ai',
  anthropic: 'ai', claude: 'ai', ml: 'ai', intelligence: 'ai',

  // nda / privacy / source
  nda: 'nda', confidential: 'nda', confidentiality: 'nda', privacy: 'nda',
  source: 'source', code: 'source', repo: 'source', repository: 'source',
  github: 'source', ownership: 'source', own: 'source', ip: 'source',

  // greetings
  hey: 'hello', hi: 'hello', hello: 'hello', namaste: 'hello', vanakkam: 'hello',
  greetings: 'hello',

  // gratitude
  thanks: 'thanks', thank: 'thanks', thx: 'thanks', appreciate: 'thanks',

  // farewell
  bye: 'bye', goodbye: 'bye', cya: 'bye',

  // affirmative
  yes: 'yes', yeah: 'yes', yep: 'yes', sure: 'yes', okay: 'yes',
  no: 'no', nope: 'no', nah: 'no',
};

const normalize = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenize = (text) => {
  const out = [];
  for (const raw of normalize(text).split(' ')) {
    if (!raw) continue;
    if (STOPWORDS.has(raw)) continue;
    out.push(SYNONYMS[raw] || raw);
  }
  return out;
};

const intents = [
  {
    id: 'greeting',
    phrases: ['hello hi'],
    reply: "Hi 👋 I'm the THINKINGWORLD assistant — the studio founded by Barani. Ask me anything about our services, projects, pricing, or how to get in touch.",
  },
  {
    id: 'thanks',
    phrases: ['thanks'],
    reply: "You're welcome! Anything else I can help with?",
  },
  {
    id: 'farewell',
    phrases: ['bye'],
    reply: "Take care 👋 When you're ready, drop us a note at thinkingworld813@gmail.com.",
  },

  {
    id: 'services',
    phrases: [
      'service offer',
      'service do',
      'service build',
      'service capability',
      'help build',
      'specialize service',
    ],
    reply:
      "We're a full-stack software studio. We build:\n• Web apps (React, Next.js, Node.js)\n• Mobile apps (React Native — iOS & Android)\n• Desktop software (Electron — Windows / Mac / Linux)\n• Backend & cloud infrastructure\n• Product design & UX\n• AI integrations (LLMs, RAG, automation)\n\nWhich one fits your project?",
  },

  {
    id: 'projects',
    phrases: [
      'project',
      'project show',
      'project see',
      'project example',
    ],
    reply:
      "Two projects we'd love to share:\n\n1️⃣  Medical Appointment System — full-stack booking platform (live)\n2️⃣  Namma Journey — travel app for South India (releasing soon)\n\nWant the live link or scroll to our Work section?",
    actions: [
      { label: 'Open Medical App', href: 'https://medical-appointment-system-4h6x.vercel.app/', external: true },
      { label: 'View on the page', scrollTo: 'projects' },
    ],
  },

  {
    id: 'medical',
    phrases: ['medical', 'medical project', 'medical service', 'medical reach'],
    reply:
      "Our Medical Appointment System is a full-stack platform with patient booking, doctor dashboards, and an admin panel. Built with React + Node.js + MongoDB. It's live now — want the link?",
    actions: [
      { label: 'Visit live site', href: 'https://medical-appointment-system-4h6x.vercel.app/', external: true },
    ],
  },

  {
    id: 'namma',
    phrases: ['namma', 'namma project', 'namma duration'],
    reply:
      "Namma Journey is our travel companion app — itineraries, hidden gems, on-trip discovery, built for the way people travel in South India. It's currently in development and releasing soon.",
  },

  {
    id: 'price',
    phrases: ['price', 'price service', 'price project'],
    reply:
      "Pricing depends on scope. Rough ranges:\n• Landing site / small tool — under ₹1L\n• MVP web or mobile app — ₹1L–₹3L\n• Full product build — ₹3L–₹10L+\n\nWe offer fixed-price quotes once requirements are clear.",
  },

  {
    id: 'duration',
    phrases: ['duration', 'duration project', 'duration service', 'duration start'],
    reply:
      "Most MVPs ship in 4–8 weeks:\n• Week 1 — discovery & scoping\n• Weeks 2–3 — design + core build\n• Weeks 4–5 — supporting flows + real data\n• Week 6 — hardening + launch\n\nWe deploy to staging weekly so you always see progress.",
  },

  {
    id: 'process',
    phrases: ['process', 'process service'],
    reply:
      "Four steps:\n1. Discover — clear scope, written plan\n2. Design — wireframes & high-fidelity mockups\n3. Build — 2-week sprints, weekly staging deploys\n4. Ship & Support — deploy, monitor, stay involved",
    actions: [{ label: 'See process', scrollTo: 'process' }],
  },

  {
    id: 'reach',
    phrases: ['reach', 'reach company'],
    reply:
      "Easiest ways to reach us:\n📧 thinkingworld813@gmail.com\n📞 +91 95979 65911 (call / WhatsApp)\n\nWe reply within 1 business day.",
    actions: [
      { label: 'Email us', href: 'mailto:thinkingworld813@gmail.com' },
      { label: 'Call now', href: 'tel:+919597965911' },
    ],
  },

  {
    id: 'tech',
    phrases: ['tech', 'tech service', 'tech project'],
    reply:
      "Our default stack:\n• Frontend — React, Next.js, React Native, Tailwind\n• Backend — Node.js, Python, Express, REST/GraphQL\n• Data — PostgreSQL, MongoDB, Redis\n• Cloud — AWS, Vercel, Render\n• Desktop — Electron, Tauri\n• AI — OpenAI, Claude, RAG pipelines",
  },

  {
    id: 'company',
    phrases: ['company', 'company reach', 'company location'],
    reply:
      "THINKINGWORLD is a small, founder-led engineering studio based in Tamil Nadu, India — founded by Barani. You talk directly to the people writing your code — no account managers, no handoffs.",
  },

  {
    id: 'location',
    phrases: ['location', 'location company'],
    reply:
      "We're based in Tamil Nadu, India and work fully remotely. Async-friendly schedules, async-friendly delivery.",
  },

  {
    id: 'start',
    phrases: ['start', 'start service', 'start project'],
    reply:
      "Easiest path:\n1. Drop a short brief at thinkingworld813@gmail.com\n2. Barani will set up a 30-min discovery call\n3. You'll get a written scope + fixed-price quote",
    actions: [{ label: 'Open contact form', scrollTo: 'contact' }],
  },

  {
    id: 'nda',
    phrases: ['nda'],
    reply:
      "Yes — happy to sign a mutual NDA before discussing anything sensitive. Just include the document with your inquiry.",
  },

  {
    id: 'source',
    phrases: ['source', 'source project'],
    reply:
      "You own every line of code we write. Full repository handover from day one — no lock-in, no surprises.",
  },

  {
    id: 'mobile',
    phrases: ['mobile', 'mobile service'],
    reply:
      "Yes — we build cross-platform mobile apps with React Native. One codebase, native performance on iOS and Android, and a fraction of the cost of building twice.",
  },

  {
    id: 'web',
    phrases: ['web', 'web service'],
    reply:
      "We build web apps with React and Next.js — fast, accessible, SEO-friendly, and built to scale. Most clients see their staging site by end of week 2.",
  },

  {
    id: 'desktop',
    phrases: ['desktop', 'desktop service'],
    reply:
      "We build cross-platform desktop apps with Electron (and Tauri when bundle size matters). Auto-updates, signed installers, offline-first design — all included.",
  },

  {
    id: 'ai',
    phrases: ['ai', 'ai service'],
    reply:
      "We integrate LLMs into products — chatbots like the one you're using, document Q&A, RAG pipelines, and decision-support tools. We use Claude and OpenAI depending on your needs.",
  },
];

// Pre-compute token sets and IDF for each intent.
const intentTokenSets = intents.map((intent) => ({
  ...intent,
  phraseTokens: intent.phrases.map((p) => new Set(tokenize(p))),
}));

const allTokens = new Map(); // token -> doc count
for (const intent of intentTokenSets) {
  const seen = new Set();
  for (const set of intent.phraseTokens) {
    for (const t of set) {
      if (!seen.has(t)) {
        seen.add(t);
        allTokens.set(t, (allTokens.get(t) || 0) + 1);
      }
    }
  }
}
const totalDocs = intentTokenSets.length;
const idf = (token) => Math.log(1 + totalDocs / (1 + (allTokens.get(token) || 0)));

export function classify(text) {
  const userTokens = tokenize(text);
  if (userTokens.length === 0) {
    return { intent: null, confidence: 0 };
  }
  const userSet = new Set(userTokens);

  let best = null;
  let bestScore = 0;

  for (const intent of intentTokenSets) {
    let intentScore = 0;
    for (const phraseSet of intent.phraseTokens) {
      let score = 0;
      let overlap = 0;
      for (const t of phraseSet) {
        if (userSet.has(t)) {
          score += idf(t);
          overlap++;
        }
      }
      if (phraseSet.size > 0) {
        const coverage = overlap / phraseSet.size;
        score *= 0.5 + coverage; // reward full coverage
      }
      if (score > intentScore) intentScore = score;
    }
    if (intentScore > bestScore) {
      bestScore = intentScore;
      best = intent;
    }
  }

  // Normalize confidence to roughly 0..1
  const confidence = Math.min(1, bestScore / 2.2);
  return { intent: best, confidence };
}

export function answerLocal(text) {
  const { intent, confidence } = classify(text);
  if (intent && confidence >= 0.34) {
    return {
      reply: intent.reply,
      actions: intent.actions || [],
      confidence,
      intent: intent.id,
    };
  }
  return {
    reply:
      "I want to give you the right answer, so let me connect you with a human. Could you email thinkingworld813@gmail.com or use the contact form? We reply within a business day.",
    actions: [
      { label: 'Email us', href: 'mailto:thinkingworld813@gmail.com' },
      { label: 'Open contact form', scrollTo: 'contact' },
    ],
    confidence,
    intent: 'fallback',
  };
}
